/**
 * Operator-persona roles for the role-scoped operations UI (epic #188).
 *
 * Single source of truth for the four operator personas. Keycloak realm
 * roles are the auth source for cloud.jeket.com; this enum gives every
 * consumer a typed value instead of stringly-typed role names.
 *
 * Note: This enum is distinct from {@link UserRole}, which is the legacy
 * "role tier" string used by older admin/proxy code. Going forward, new
 * code should consume {@link Role} via `req.user.roles`.
 */
export var Role;
(function (Role) {
    Role["EventOperator"] = "event-operator";
    Role["SiteOperator"] = "site-operator";
    Role["Engineer"] = "engineer";
    Role["Admin"] = "admin";
})(Role || (Role = {}));
/**
 * Hierarchy used by {@link canActAs}. Higher index = greater authority.
 * Admin can act as any lower role; Engineer can act as Site/EventOperator;
 * SiteOperator can act as EventOperator; EventOperator only acts as itself.
 */
const ROLE_HIERARCHY = [
    Role.EventOperator,
    Role.SiteOperator,
    Role.Engineer,
    Role.Admin,
];
const KNOWN_ROLE_VALUES = new Set(Object.values(Role));
/**
 * The persona that operates a cloud.jeket.com source (JSS#1001): it creates,
 * edits and deletes sources, and only it is shown a source's stream key. The
 * BFF gates source writes on it, channel-service mirrors that gate and drops
 * `streamKey` from its answers to anyone below it, and the Sources page hides
 * the same controls and the key behind `<RoleGate>`. The lowest persona, so
 * any operator in the org qualifies and a Viewer (no persona role) does not.
 */
export const SOURCE_OPERATOR_ROLE = Role.EventOperator;
/**
 * The persona that runs a show's guests: approves, rejects, promotes, demotes
 * and removes participants, toggles a guest's camera or mic, and fetches a
 * guest's tokens on their behalf. #188 gives show operation to the Event
 * Operator (the show-affecting workload restart is gated on it too), and it is
 * the lowest persona, so any operator in the org qualifies and a Viewer does
 * not. The BFF gates these routes on it (orchestrator src/index.ts) and
 * event-manager mirrors the gate from `X-User-Roles`. Guests act on their own
 * participant with their `X-Participant-Token` instead.
 */
export const PARTICIPANT_MODERATOR_ROLE = Role.EventOperator;
/**
 * Filter an arbitrary list of role strings (e.g. from a JWT's
 * `realm_access.roles`) down to the known {@link Role} enum values.
 * Unknown role strings are dropped silently — they are not an error.
 */
export function filterKnownRoles(roles) {
    const out = [];
    for (const r of roles) {
        if (KNOWN_ROLE_VALUES.has(r))
            out.push(r);
    }
    return out;
}
/** True iff `value` is one of the {@link Role} enum values. */
export function isRole(value) {
    return typeof value === 'string' && KNOWN_ROLE_VALUES.has(value);
}
/**
 * Strict membership check: returns true iff the caller's role list contains
 * the exact required role.
 */
export function hasRole(roles, required) {
    return roles.includes(required);
}
/**
 * Hierarchical capability check: returns true iff the caller holds the
 * required role OR any role above it in the hierarchy
 * (Admin > Engineer > SiteOperator > EventOperator).
 *
 * Use this for "can the caller perform an action that needs at least
 * SiteOperator authority?" — Admins and Engineers also pass.
 */
export function canActAs(roles, required) {
    const requiredIdx = ROLE_HIERARCHY.indexOf(required);
    if (requiredIdx < 0)
        return false;
    for (const r of roles) {
        const idx = ROLE_HIERARCHY.indexOf(r);
        if (idx >= requiredIdx)
            return true;
    }
    return false;
}
/**
 * Pick the highest-authority {@link Role} from a list of role strings,
 * using the same hierarchy as {@link canActAs}
 * (Admin > Engineer > SiteOperator > EventOperator).
 *
 * Unknown role strings are ignored. Returns `null` if no known role is
 * present — callers (e.g. audit emitters) decide how to handle that case
 * (the audit schema requires a single Role, so callers should fall back
 * to a sensible default like {@link Role.EventOperator} or refuse to emit).
 */
export function highestRole(roles) {
    let bestIdx = -1;
    for (const r of roles) {
        const idx = ROLE_HIERARCHY.indexOf(r);
        if (idx > bestIdx)
            bestIdx = idx;
    }
    return bestIdx >= 0 ? ROLE_HIERARCHY[bestIdx] : null;
}
/**
 * The org aliases in a token's `organization` claim, lower-cased and
 * de-duplicated, in claim order. Accepts both claim shapes; anything else
 * (absent, malformed) is no membership. Keycloak org aliases equal JSS
 * `organizations.slug` (the BFF creates both from one value), so an alias is
 * a slug.
 */
export function organizationAliases(claim) {
    let raw = [];
    if (Array.isArray(claim))
        raw = claim;
    else if (typeof claim === 'string')
        raw = [claim];
    else if (claim && typeof claim === 'object')
        raw = Object.keys(claim);
    const out = [];
    for (const a of raw) {
        if (typeof a !== 'string')
            continue;
        const alias = a.trim().toLowerCase();
        if (alias !== '' && !out.includes(alias))
            out.push(alias);
    }
    return out;
}
//# sourceMappingURL=auth.js.map